'use client';

import { faXmark } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { CART_CLEAR_CONFIRMATION_COPY } from './cart-clear-confirmation.const';

import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { Modal } from '@/components/ui/modal.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { CartClearReason } from '@/types/cart-clear-confirmation.types';

export interface CartClearConfirmationModalProps {
  open: boolean;
  reason: CartClearReason | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export function CartClearConfirmationModal({
  open,
  reason,
  onConfirm,
  onCancel,
}: CartClearConfirmationModalProps) {
  if (!reason) {
    return null;
  }

  const { title, description, confirmLabel, cancelLabel } = CART_CLEAR_CONFIRMATION_COPY[reason];

  return (
    <Modal open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <Modal.Content
        title={title}
        data-testid={DataTestIdEnum.CART_CLEAR_CONFIRMATION_MODAL}
        slotHeader={(
          <header className="flex w-full items-start justify-between gap-2 pt-5 pr-5 pb-4 pl-6">
            <h2
              className="text-2xl leading-8 font-bold text-gray-900"
              data-testid={DataTestIdEnum.CART_CLEAR_CONFIRMATION_TITLE}
            >
              {title}
            </h2>

            <Modal.Close
              aria-label="Fechar"
              className="flex size-8 shrink-0 items-center justify-center rounded-sm text-gray-500 transition-opacity hover:opacity-70 focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:outline-none"
              data-testid={DataTestIdEnum.CART_CLEAR_CONFIRMATION_CLOSE_BUTTON}
            >
              <Icon icon={faXmark} className="text-2xl" />
            </Modal.Close>
          </header>
        )}
      >
        <div className="flex flex-col gap-6 px-6 pb-5">
          <Typography
            variant={TypographyVariant.BODY_TEXT}
            data-testid={DataTestIdEnum.CART_CLEAR_CONFIRMATION_DESCRIPTION}
          >
            {description}
          </Typography>

          <div className="flex flex-col gap-4">
            <Button
              color="danger"
              data-testid={DataTestIdEnum.CART_CLEAR_CONFIRMATION_CONFIRM_BUTTON}
              onClick={onConfirm}
            >
              {confirmLabel}
            </Button>

            <Button
              variant="secondary"
              color="primary"
              className="leading-6"
              data-testid={DataTestIdEnum.CART_CLEAR_CONFIRMATION_CANCEL_BUTTON}
              onClick={onCancel}
            >
              {cancelLabel}
            </Button>
          </div>
        </div>
      </Modal.Content>
    </Modal>
  );
}
